/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {generateUUID} from '@dvrd/dvr-controls';
import {ResponseData} from '@dvrd/fetch';
import {ORANGE_VUURVOGEL} from '../util/constants';
import {DefaultListParams, ModelResponse} from '../util/interfaces';
import {getNavBlocks} from '../util/requestSender';
import {deepClone} from '../util/utils';
import {BaseFields, BaseModel, BaseProps, FieldDefinition, ModelFieldType} from './baseModel';

interface Props extends BaseProps {
    label?: string;
    background_color?: string | null;
    color?: string | null;
    url?: string | null;
    active?: boolean;
    index?: number;
    image?: string | null;
}

export interface NavBlockFields extends BaseFields {
    label: string;
    background_color: string | null;
    color: string | null;
    url: string | null;
    active: boolean;
    index: number;
    image: string | null;
}

const fields: NavBlockFields = {
    id: '',
    created_at: '',
    updated_at: '',
    label: '',
    background_color: null,
    color: null,
    url: null,
    active: true,
    index: -1,
    image: null
};

type FieldDefType = keyof NavBlockFields;

const fieldsDefinition: Record<FieldDefType, FieldDefinition> = {
    id: new FieldDefinition(ModelFieldType.STRING),
    created_at: new FieldDefinition(ModelFieldType.STRING),
    updated_at: new FieldDefinition(ModelFieldType.STRING, {allowNull: true}),
    label: new FieldDefinition(ModelFieldType.STRING),
    background_color: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, defaultValue: null}),
    color: new FieldDefinition(ModelFieldType.STRING, {allowNull: true, defaultValue: null}),
    url: new FieldDefinition(ModelFieldType.STRING, {allowNull: true}),
    active: new FieldDefinition(ModelFieldType.BOOLEAN),
    index: new FieldDefinition(ModelFieldType.NUMBER),
    image: new FieldDefinition(ModelFieldType.STRING, {allowNull: true})
};

const ignoreProps: Array<keyof NavBlockFields> = [];

type GetAllConfig = DefaultListParams & { active?: boolean; };

export default class NavBlock extends BaseModel<Props, NavBlockFields, FieldDefType> {
    static getAll = (config: GetAllConfig, callback: ModelResponse<Array<NavBlock>>) => {
        return getNavBlocks({
            data: {
                limit: config.limit, active: config.active, search: config.search ?? null, after_id: config.afterID,
                before_id: config.beforeID
            }, callback: (data: ResponseData) => {
                const blocks: Array<NavBlock> = data.success ? data.header_blocks.map(
                    (block: any) => new NavBlock(block)) : [];
                callback(blocks, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
        this.identity = props?.identity ?? `NavBlock.${props?.id ?? generateUUID()}`;
    }

    get label(): string {
        return this.fields.label;
    }

    set label(value: string) {
        this.fields.label = value;
    }

    get backgroundColor(): string {
        return this.fields.background_color ?? ORANGE_VUURVOGEL;
    }

    set backgroundColor(value: string | null) {
        this.fields.background_color = value;
    }

    get color(): string {
        return this.fields.color ?? 'white';
    }

    set color(value: string | null) {
        this.fields.color = value;
    }

    get url(): string | null {
        return this.fields.url;
    }

    set url(value: string | null) {
        this.fields.url = value;
    }

    get active(): boolean {
        return this.fields.active;
    }

    set active(value: boolean) {
        this.fields.active = value;
    }

    get index(): number {
        return this.fields.index;
    }

    set index(value: number) {
        this.fields.index = value;
    }

    get image(): string | null {
        return this.fields.image;
    }

    set image(value: string | null) {
        this.fields.image = value;
    }

    clone = (identityMap?: Map<string, any>): NavBlock => {
        const props = deepClone(this.fields, identityMap);
        return new NavBlock({...props, identity: this.identity});
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}